import {useRef, useEffect} from "react";
import ProgressBar from "../progressBar/ProgressBar";
import LevelBlockStyles from "./LevelBlockStyles.module.css";
import PlanetLevelBg from "../../assets/img/dashboard/levels-section-bg.png";
import PlanetLevel from "../../assets/img/dashboard/planet-levels.png";
import {LevelBlockProps} from "../../types/ComponentProps";
import {getLevel} from "../../utils/getLevel";

import AsteroidAdventurer from "../../assets/img/dashboard/level-badge/asteroid-adventurer-avatar.png";
import CosmicCatalyst from "../../assets/img/dashboard/level-badge/cosmic-catalyst-avatar.png";
import ETExplorer from "../../assets/img/dashboard/level-badge/extraterrestrial-explorer-avatar.png";
import FusionForeRunner from "../../assets/img/dashboard/level-badge/fusion-forerunner-avatar.png";
import GalacticGroundbreaker from "../../assets/img/dashboard/level-badge/galactic-groundbreaker-avatar.png";
import InterplanetaryIconoclast from "../../assets/img/dashboard/level-badge/interplanetary-iconocolast-avatar.png";
import InterstellarInitiator from "../../assets/img/dashboard/level-badge/interstellar-initiator-avatar.png";
import IntergalacticInnovator from "../../assets/img/dashboard/level-badge/intergalactic-innovator-avatar.png";
import InterCelestialInventor from "../../assets/img/dashboard/level-badge/intercelestial-inventor-avatar.png";
import LunarLeader from "../../assets/img/dashboard/level-badge/lunar-leader-avatar.png";
import LightYearLuminary from "../../assets/img/dashboard/level-badge/light-year-luminary-avatar.png";
import NebularNavigator from "../../assets/img/dashboard/level-badge/nebular-navigator-avatar.png";
import PlanetaryPathfinder from "../../assets/img/dashboard/level-badge/planetary-pathfinder-avatar.png";
import PulsarPioneer from "../../assets/img/dashboard/level-badge/pulsar-pioneer-avatar.png";
import StardustTrailblazer from "../../assets/img/dashboard/level-badge/stardust-trailblazer-avatar.png";
import StellarTrendsetter from "../../assets/img/dashboard/level-badge/stellar-trendsetter-avatar.png";
import SupernovaVanguard from "../../assets/img/dashboard/level-badge/supernova-vanguard-avatar.png";
import UltravioletVisionary from "../../assets/img/dashboard/level-badge/ultraviolet-visionary-avatar.png";
import ZeroGWayfarer from "../../assets/img/dashboard/level-badge/zero-g-wayfarer-avatar.png";

const levelImages: Record<string, string> = {
  "Asteroid Adventurer": AsteroidAdventurer,
  "Cosmic Catalyst": CosmicCatalyst,
  "Extraterrestrial Explorer": ETExplorer,
  "Fusion Forerunner": FusionForeRunner,
  "Galactic Groundbreaker": GalacticGroundbreaker,
  "Interplanetary Iconoclast": InterplanetaryIconoclast,
  "Interstellar Initiator": InterstellarInitiator,
  "Intergalactic Innovator": IntergalacticInnovator,
  "Inter-celestial Inventor": InterCelestialInventor,
  "Lunar Leader": LunarLeader,
  "Light-year Luminary": LightYearLuminary,
  "Nebular Navigator": NebularNavigator,
  "Planetary Pathfinder": PlanetaryPathfinder,
  "Pulsar Pioneer": PulsarPioneer,
  "Stardust Trailblazer": StardustTrailblazer,
  "Stellar Trendsetter": StellarTrendsetter,
  "Supernova Vanguard": SupernovaVanguard,
  "Ultraviolet Visionary": UltravioletVisionary,
  "Zero-G Wayfarer": ZeroGWayfarer,
};

const getPointsRange = (level: string): string | null => {
  switch (level) {
    case "Asteroid Adventurer":
      return "0 - 349 Points";
    case "Cosmic Catalyst":
      return "350 - 899 Points";
    case "Extraterrestrial Explorer":
      return "900 - 1799 Points";
    case "Fusion Forerunner":
      return "1800 - 3499 Points";
    case "Galactic Groundbreaker":
      return "3500 - 5499 Points";
    case "Interplanetary Iconoclast":
      return "5500 - 7999 Points";
    case "Interstellar Initiator":
      return "8000 - 11999 Points";
    case "Intergalactic Innovator":
      return "12000 - 17999 Points";
    case "Inter-celestial Inventor":
      return "18000 - 22999 Points";
    case "Lunar Leader":
      return "23000 - 27999 Points";
    case "Light-year Luminary":
      return "28000 - 34999 Points";
    case "Nebular Navigator":
      return "35000 - 44999 Points";
    case "Planetary Pathfinder":
      return "45000 - 59999 Points";
    case "Pulsar Pioneer":
      return "60000 - 74999 Points";
    case "Stardust Trailblazer":
      return "75000 - 92999 Points";
    case "Stellar Trendsetter":
      return "93000 - 99999 Points";
    case "Supernova Vanguard":
      return "100000 - 100599 Points";
    case "Ultraviolet Visionary":
      return "100600 - 101199 Points";
    case "Zero-G Wayfarer":
      return "101200 - 999999 Points";
    default:
      return null;
  }
};

const LevelBlock: React.FC<LevelBlockProps> = ({
  data,
  isOpenLevel,
  setIsOpenLevel,
}) => {
  const activeLevelRef = useRef<HTMLDivElement | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const {points} = data || {};
  const levelName = getLevel(points?.points);
  const levelImage = levelImages[levelName?.level || ""];
  const levelIndex = levelName?.index != null ? levelName.index + 1 : null;
  const percentage = points?.points === 0 ? 0 : levelName?.percentage ?? 0;

  const onToggleLevelClick = () => {
    setIsOpenLevel?.((prev) => !prev);
  };

  const scrollToActive = () => {
    if (activeLevelRef.current && scrollContainerRef.current) {
      const containerTop =
        scrollContainerRef.current.getBoundingClientRect().top;
      const activeTop = activeLevelRef.current.getBoundingClientRect().top;
      scrollContainerRef.current.scrollTop += activeTop - containerTop;
    }
  };

  useEffect(() => {
    scrollToActive();
  }, [isOpenLevel, levelIndex]);

  return (
    <div className={LevelBlockStyles.levelMainContainerBlk}>
      <div
        className={LevelBlockStyles.levelContainer}
        style={{
          borderBottomRightRadius: isOpenLevel ? "0px" : "15px",
          borderBottomLeftRadius: isOpenLevel ? "0px" : "15px",
        }}>
        <img
          alt="Planet One"
          className={LevelBlockStyles.planetLevelImageBg}
          src={PlanetLevel}
        />
        <img
          alt="Planet Two"
          className={LevelBlockStyles.planetLevelIBg}
          src={PlanetLevelBg}
        />

        <div className={LevelBlockStyles.levelDescriptionBlk}>
          <div className={LevelBlockStyles.flexBoxBlk}>
            <h1>My Level</h1>
            <button
              onClick={onToggleLevelClick}
              aria-label={isOpenLevel ? "Collapse section" : "Expand section"}
              aria-expanded={isOpenLevel}>
              <i
                className={`fa-solid ${
                  isOpenLevel ? "fa-chevron-up" : "fa-chevron-down"
                }`}
                aria-hidden="true"
              />
            </button>
          </div>
          <div className={LevelBlockStyles.flexBox}>
            {levelImage && (
              <div>
                <img alt="Level Avatar" src={levelImage} />
              </div>
            )}
            <div>
              <h2>Level {levelIndex}</h2>
              <p>{levelName?.level}</p>
              <ProgressBar width={percentage} points={points?.points} />
            </div>
          </div>
        </div>
      </div>

      {isOpenLevel && (
        <div className={LevelBlockStyles.levelListBlock}>
          <h1>Levels List</h1>
          <div className={LevelBlockStyles.levelList} ref={scrollContainerRef}>
            {Object.entries(levelImages).map(([level, image], index) => {
              const isActive = levelIndex === index + 1;
              const range = getPointsRange(level);
              return (
                <div
                  key={level}
                  className={LevelBlockStyles.flexBoxBlk}
                  ref={isActive ? activeLevelRef : null}
                  style={{opacity: isActive ? 1 : 0.3}}>
                  <div>
                    <img alt={level} src={image} />
                  </div>
                  <div>
                    <h1>
                      Level {index + 1}{" "}
                      {isActive && (
                        <span>
                          <i className="fa-solid fa-star" />
                        </span>
                      )}
                    </h1>
                    <h2>{level}</h2>
                    {range && <p>{range}</p>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default LevelBlock;
